import { buildAppVisualReferencesForAi } from './buildAppVisualReferencesForAi';
import {
  extractSiblingContentCovers,
  extractSiblingModuleCovers,
} from './coverAiReferenceOptions';
import {
  getCoverAiAttachmentUrls,
  getCoverAiAttachmentUrlsFromSelection,
  DEFAULT_TEMPLATE_FILENAME,
} from './aiAssistantReferenceImages';
import {
  buildCoverImageRulesBlock,
  buildPublicImageUrlNotice,
  buildReferenceImageAttachmentNotice,
  buildTemplateCloneInstructions,
  formatContentTypeLabel,
  formatModuleCoverReference,
  formatOrientationLabel,
  formatPublicImageUrlLine,
  formatSecondaryBrandContextBlock,
} from './coverImageAiPromptShared';

function formatSiblingModuleCoversSection(
  covers,
  categoryTitle,
  omittedCount = 0,
  selectedReferenceUrl = null
) {
  const lines = covers.map((cover) => {
    const selectedMark =
      selectedReferenceUrl && cover.url === selectedReferenceUrl ? ' ← TEMPLATE MASTER (anexado)' : '';
    return `- Módulo: ${cover.moduleTitle} — Categoria: ${categoryTitle}\n${formatPublicImageUrlLine(cover.url)}${selectedMark}`;
  });

  if (omittedCount > 0) {
    lines.push(`(+${omittedCount} capas de módulos omitidas nesta categoria)`);
  }

  if (!lines.length) {
    return `Capas de outros módulos na categoria "${categoryTitle}" (REFERÊNCIA PRINCIPAL — template a replicar):
(nenhuma capa de referência encontrada — peça referências ao usuário)`;
  }

  return `Capas de outros módulos na mesma categoria "${categoryTitle}" (REFERÊNCIA PRINCIPAL — replique este template, troque só o título):
${lines.length} capa(s) de referência:
${lines.join('\n')}`;
}

function formatSiblingContentCoversSection(
  covers,
  moduleTitle,
  omittedCount = 0,
  selectedReferenceUrl = null
) {
  const lines = covers.map((cover) => {
    const selectedMark =
      selectedReferenceUrl && cover.url === selectedReferenceUrl ? ' ← TEMPLATE MASTER (anexado)' : '';
    return `- ${cover.contentType}: ${cover.contentTitle} — Módulo: ${moduleTitle}\n${formatPublicImageUrlLine(cover.url)}${selectedMark}`;
  });

  if (omittedCount > 0) {
    lines.push(`(+${omittedCount} capas omitidas neste módulo)`);
  }

  if (!lines.length) {
    return `Capas de outros conteúdos neste módulo "${moduleTitle}" (REFERÊNCIA PRINCIPAL — template a replicar):
(nenhuma capa de referência encontrada — use a capa do módulo abaixo ou peça referências ao usuário)`;
  }

  return `Capas de outros conteúdos/aulas ao vivo neste módulo "${moduleTitle}" (REFERÊNCIA PRINCIPAL — replique este template, troque só o título):
${lines.length} capa(s) de referência:
${lines.join('\n')}`;
}

function resolveSelectedReference(selectedReference, fallbackUrl, fallbackKind) {
  if (selectedReference?.url) return selectedReference;
  if (!fallbackUrl) return null;
  return { url: fallbackUrl, label: null, kind: fallbackKind };
}

/**
 * Prompt para capa de módulo/card — referencia outras capas da mesma categoria.
 */
export function buildModuleCoverAiAssistantPayload({
  moduleTitle = '',
  categoryTitle = '',
  cardId = '',
  orientation = 'horizontal',
  siblingCards = [],
  visualContext = {},
  selectedReference = null,
} = {}) {
  const orientationLabel = formatOrientationLabel(orientation);
  const aspectRatioLabel =
    orientation === 'vertical'
      ? 'vertical (formato da capa de catálogo — como as referências da categoria)'
      : 'horizontal 16:9';

  const { items: siblingCovers, omittedCount } = extractSiblingModuleCovers(siblingCards, {
    excludeCardId: cardId,
    orientation,
  });

  const resolvedReference = resolveSelectedReference(
    selectedReference,
    siblingCovers[0]?.url || null,
    'sibling-module'
  );
  const templateMasterUrl = resolvedReference?.url || null;
  const strictSiblingTemplate = resolvedReference?.kind === 'sibling-module' && siblingCovers.length > 0;

  const appVisualReferences = buildAppVisualReferencesForAi(visualContext);
  const siblingBlock = formatSiblingModuleCoversSection(
    siblingCovers,
    categoryTitle || '(sem título)',
    omittedCount,
    templateMasterUrl
  );

  const resolvedModuleTitle = moduleTitle || '(sem título)';

  const attachmentUrls = resolvedReference
    ? getCoverAiAttachmentUrlsFromSelection(resolvedReference)
    : getCoverAiAttachmentUrls({ templateMasterUrl, siblingCount: siblingCovers.length });
  const attachmentFilename = attachmentUrls[0]?.filename || DEFAULT_TEMPLATE_FILENAME;
  const hasAttachment = attachmentUrls.length > 0;

  const cloneBlock = buildTemplateCloneInstructions({
    coverTitle: resolvedModuleTitle,
    templateMasterUrl,
    siblingCount: siblingCovers.length,
    scopeLabel: 'categoria',
    attachmentFilename,
    selectedReferenceLabel: resolvedReference?.label,
  });
  const referenceNotice = hasAttachment
    ? buildReferenceImageAttachmentNotice({
        attachmentFilename,
        siblingCount: siblingCovers.length,
      })
    : buildPublicImageUrlNotice({ templateMasterUrl, siblingCount: siblingCovers.length });

  const rulesBlock = buildCoverImageRulesBlock({
    aspectRatioLabel,
    subjectLabel: `capa ${orientationLabel} do módulo "${resolvedModuleTitle}" na categoria "${categoryTitle || '(sem título)'}"`,
    referenceLabel: 'categoria',
    coverTitle: resolvedModuleTitle,
    coverTitleLabel: 'Título do módulo na capa',
    strictSiblingTemplate,
    attachmentFilename: hasAttachment ? attachmentFilename : null,
  });

  const referencesBlock = [siblingBlock, formatSecondaryBrandContextBlock(appVisualReferences)]
    .filter(Boolean)
    .join('\n\n');

  const selectedNote = resolvedReference?.label
    ? `\nCapa de referência escolhida pelo admin: "${resolvedReference.label}"\n`
    : '';

  const text = `${referenceNotice}

Preciso criar a capa ${orientationLabel} de um módulo (card) no app.
${selectedNote}${cloneBlock ? `\n${cloneBlock}\n` : ''}
Título do módulo (OBRIGATÓRIO na capa — copiar exatamente):
${resolvedModuleTitle}

Categoria: ${categoryTitle || '(sem título)'}

Referências visuais complementares (URLs de backup — a imagem anexada "${attachmentFilename}" tem prioridade):
${referencesBlock}

Se precisar de material extra, peça Branding Book, identidade visual ou fotos adicionais ao usuário.

${rulesBlock}

Formato da resposta (obrigatório, em português):

0) A imagem "${attachmentFilename}" está anexada nesta conversa e você conseguiu analisá-la?
(sim / não — se não, peça o anexo antes de gerar; NÃO invente estilo)

1) Capa sugerida:
(gerada / brief para produção)
Texto na capa: "${resolvedModuleTitle}"

2) Como seguiu as referências:
- Paleta/cores usadas: …
- Elementos replicados das capas da categoria: …

3) Observações (opcional):
…

O admin fará upload manual da capa no painel (Módulo → Nova Capa ${orientation === 'vertical' ? 'Vertical' : 'Horizontal'}).`;

  return { text, attachmentUrls };
}

export function buildModuleCoverAiAssistantText(options = {}) {
  return buildModuleCoverAiAssistantPayload(options).text;
}

/**
 * Prompt para capa de conteúdo/aula ao vivo — referencia outras capas do mesmo módulo.
 */
export function buildContentCoverAiAssistantPayload({
  contentTitle = '',
  contentType = 'content',
  moduleTitle = '',
  moduleCoverUrl = null,
  excludeVideoId = '',
  videos = [],
  visualContext = {},
  selectedReference = null,
} = {}) {
  const contentTypeLabel = formatContentTypeLabel(contentType);
  const resolvedContentTitle = contentTitle || '(sem título)';
  const resolvedModuleTitle = moduleTitle || '(sem título)';
  const { items: siblingCovers, omittedCount } = extractSiblingContentCovers(videos, {
    excludeVideoId,
  });

  const defaultReferenceUrl = siblingCovers[0]?.url || moduleCoverUrl || null;
  const defaultKind = siblingCovers[0]?.url ? 'sibling-content' : 'module-cover';
  const resolvedReference = resolveSelectedReference(
    selectedReference,
    defaultReferenceUrl,
    defaultKind
  );
  const templateMasterUrl = resolvedReference?.url || null;
  const strictSiblingTemplate =
    resolvedReference?.kind === 'sibling-content' && siblingCovers.length > 0;

  const appVisualReferences = buildAppVisualReferencesForAi(visualContext);
  const siblingBlock = formatSiblingContentCoversSection(
    siblingCovers,
    resolvedModuleTitle,
    omittedCount,
    templateMasterUrl
  );
  const moduleCoverBlock =
    resolvedReference?.kind === 'module-cover'
      ? `Capa do módulo "${resolvedModuleTitle}" (REFERÊNCIA PRINCIPAL — template anexado):\n${formatPublicImageUrlLine(moduleCoverUrl)} ← TEMPLATE MASTER (anexado)`
      : formatModuleCoverReference(resolvedModuleTitle, moduleCoverUrl);

  const attachmentUrls = resolvedReference
    ? getCoverAiAttachmentUrlsFromSelection(resolvedReference)
    : getCoverAiAttachmentUrls({
        templateMasterUrl: siblingCovers[0]?.url || null,
        moduleCoverUrl,
        siblingCount: siblingCovers.length,
      });
  const attachmentFilename = attachmentUrls[0]?.filename || DEFAULT_TEMPLATE_FILENAME;
  const hasAttachment = attachmentUrls.length > 0;

  const cloneBlock = buildTemplateCloneInstructions({
    coverTitle: resolvedContentTitle,
    templateMasterUrl,
    siblingCount: strictSiblingTemplate ? siblingCovers.length : 0,
    scopeLabel: 'módulo',
    attachmentFilename: hasAttachment ? attachmentFilename : DEFAULT_TEMPLATE_FILENAME,
    selectedReferenceLabel: resolvedReference?.label,
  });
  const referenceNotice = hasAttachment
    ? buildReferenceImageAttachmentNotice({
        attachmentFilename,
        siblingCount: siblingCovers.length,
      })
    : buildPublicImageUrlNotice({ templateMasterUrl, siblingCount: siblingCovers.length });

  const rulesBlock = buildCoverImageRulesBlock({
    aspectRatioLabel: 'horizontal ~16:9 (mesma proporção das miniaturas da lista de conteúdos)',
    subjectLabel: `${contentTypeLabel}: "${resolvedContentTitle}" no módulo "${resolvedModuleTitle}"`,
    referenceLabel: 'módulo',
    coverTitle: resolvedContentTitle,
    coverTitleLabel: `Título do ${contentTypeLabel.toLowerCase()} na capa`,
    strictSiblingTemplate,
    attachmentFilename: hasAttachment ? attachmentFilename : null,
  });

  const referencesBlock = [
    siblingBlock,
    moduleCoverBlock,
    formatSecondaryBrandContextBlock(appVisualReferences),
  ]
    .filter(Boolean)
    .join('\n\n');

  const siblingCountNote =
    siblingCovers.length > 0
      ? `\nExistem ${siblingCovers.length} capa(s) de referência neste módulo com o MESMO design — clone o template anexado e troque só o título.\n`
      : moduleCoverUrl
        ? `\nNão há outras capas neste módulo — use a imagem anexada da capa do módulo como referência.\n`
        : '\nNão há outras capas neste módulo — peça referências ao usuário.\n';

  const selectedNote = resolvedReference?.label
    ? `\nCapa de referência escolhida pelo admin: "${resolvedReference.label}"\n`
    : '';

  const text = `${referenceNotice}

Preciso criar a capa (miniatura) de um conteúdo no app.
${siblingCountNote}${selectedNote}${cloneBlock ? `\n${cloneBlock}\n` : ''}
Título do ${contentTypeLabel.toLowerCase()} (OBRIGATÓRIO na capa — copiar exatamente):
${resolvedContentTitle}

Tipo: ${contentTypeLabel}
Módulo: ${resolvedModuleTitle}

Referências visuais complementares (URLs de backup — a imagem anexada "${attachmentFilename}" tem prioridade):
${referencesBlock}

Se precisar de material extra, peça Branding Book ou fotos adicionais — mas NÃO mude o template se as capas irmãs já definem o padrão.

${rulesBlock}

Formato da resposta (obrigatório, em português):

0) A imagem "${attachmentFilename}" está anexada nesta conversa e você conseguiu analisá-la?
(sim / não — se não, peça o anexo antes de gerar; NÃO invente estilo)

1) Padrão visual identificado na imagem anexada:
(descreva: fundo, cores hex, gráficos, tipografia — confirme clone 100%)
2) Capa sugerida:
(gerada / brief para produção — clone do template anexado com novo título)
Texto na capa: "${resolvedContentTitle}"

3) Checklist de fidelidade:
- [ ] Mesma cor de fundo que o template
- [ ] Mesmos gráficos/ícones nas mesmas posições
- [ ] Mesma tipografia (família, peso, cor)
- [ ] Só o título mudou para "${resolvedContentTitle}"
- [ ] Nenhum elemento novo (fotos, sombras, folhas, gradientes)

4) Observações (opcional):
…

O admin fará upload manual da capa no painel (lista de conteúdos → Alterar capa).`;

  return { text, attachmentUrls };
}

export function buildContentCoverAiAssistantText(options = {}) {
  return buildContentCoverAiAssistantPayload(options).text;
}

export {
  buildModuleCoverReferenceOptions,
  buildContentCoverReferenceOptions,
} from './coverAiReferenceOptions';
